import { TulaSDK } from './tula-sdk';
import { ItemVariantResponse } from './types';
import { debugLog } from './utils';

interface TrackedElement {
  element: Element;
  tracked: boolean;
  itemVariant?: ItemVariantResponse;
}

export class AutoTracker {
  private sdk: TulaSDK;
  private observer: MutationObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private trackedElements = new WeakMap<Element, TrackedElement>();
  private debug: boolean;

  constructor(sdk: TulaSDK, debug: boolean = false) {
    this.sdk = sdk;
    this.debug = debug;
  }

  start(): void {
    // Universal environment detection for all JS mobile game engines
    const environment = this.detectEnvironment();
    
    if (!environment.supportsDOMTracking) {
      debugLog(this.debug, `AutoTracker: ${environment.name} detected, DOM auto-tracking not supported. Use manual tracking methods.`);
      return;
    }

    this.setupMutationObserver();
    this.setupIntersectionObserver();
    this.scanExistingElements();

    debugLog(this.debug, `AutoTracker: Started DOM-based auto-tracking in ${environment.name}`);
  }

  private detectEnvironment(): { name: string; supportsDOMTracking: boolean } {
    // React Native detection
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
      return { name: 'React Native', supportsDOMTracking: false };
    }

    // Cocos2d detection (has cc global object)
    if (typeof window !== 'undefined' && (window as any).cc && (window as any).cc.game) {
      return { name: 'Cocos2d', supportsDOMTracking: false };
    }

    // Unity WebGL detection (has unityInstance or Module)
    if (typeof window !== 'undefined' && 
        ((window as any).unityInstance || (window as any).Module || (window as any).UnityLoader)) {
      return { name: 'Unity WebGL', supportsDOMTracking: true };
    }

    // Phaser detection
    if (typeof window !== 'undefined' && (window as any).Phaser) {
      return { name: 'Phaser', supportsDOMTracking: true };
    }

    // PlayCanvas detection
    if (typeof window !== 'undefined' && (window as any).pc) {
      return { name: 'PlayCanvas', supportsDOMTracking: true };
    }

    // Babylon.js detection
    if (typeof window !== 'undefined' && (window as any).BABYLON) {
      return { name: 'Babylon.js', supportsDOMTracking: true };
    }

    // Three.js detection
    if (typeof window !== 'undefined' && (window as any).THREE) {
      return { name: 'Three.js', supportsDOMTracking: true };
    }

    // Node.js detection
    if (typeof window === 'undefined' && typeof global !== 'undefined' && typeof process !== 'undefined') {
      return { name: 'Node.js', supportsDOMTracking: false };
    }

    // Browser detection (check for required DOM APIs)
    if (typeof window !== 'undefined' && 
        typeof document !== 'undefined' && 
        typeof MutationObserver !== 'undefined' &&
        typeof IntersectionObserver !== 'undefined') {
      return { name: 'Browser', supportsDOMTracking: true };
    }

    // Generic JS engine (like V8 in mobile apps)
    if (typeof window === 'undefined' && typeof document === 'undefined') {
      return { name: 'Native JS Engine', supportsDOMTracking: false };
    }

    // Fallback for unknown environments
    return { name: 'Unknown Environment', supportsDOMTracking: false };
  }

  stop(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    debugLog(this.debug, 'AutoTracker: Stopped auto-tracking');
  }

  private setupMutationObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.scanElement(node as Element);
          }
        });
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handleElementVisible(entry.target);
          }
        });
      },
      {
        threshold: 0.5, // Element must be 50% visible
        rootMargin: '0px'
      }
    );
  }

  private scanExistingElements(): void {
    // Scan existing store elements
    const storeElements = document.querySelectorAll('[data-tula-store]');
    storeElements.forEach((element) => this.handleStoreElement(element));

    // Scan existing item elements  
    const itemElements = document.querySelectorAll('[data-tula-item]');
    itemElements.forEach((element) => this.handleItemElement(element));
  }

  private scanElement(element: Element): void {
    // Check if this element or any child has tracking attributes
    if (element.hasAttribute('data-tula-store')) {
      this.handleStoreElement(element);
    }

    if (element.hasAttribute('data-tula-item')) {
      this.handleItemElement(element);
    }

    // Scan children
    const storeChildren = element.querySelectorAll('[data-tula-store]');
    storeChildren.forEach((child) => this.handleStoreElement(child));

    const itemChildren = element.querySelectorAll('[data-tula-item]');
    itemChildren.forEach((child) => this.handleItemElement(child));
  }

  private handleStoreElement(element: Element): void {
    const tracked = this.trackedElements.get(element);
    if (tracked?.tracked) {
      return; // Already tracked
    }

    // Track immediately for store views (no need to wait for visibility)
    this.trackStoreView(element);
  }

  private handleItemElement(element: Element): void {
    const tracked = this.trackedElements.get(element);
    if (tracked?.tracked) {
      return; // Already tracked
    }

    // Set up for visibility tracking
    this.trackedElements.set(element, {
      element,
      tracked: false,
      itemVariant: this.parseItemVariant(element)
    });

    if (this.intersectionObserver) {
      this.intersectionObserver.observe(element);
    }
  }

  private handleElementVisible(element: Element): void {
    const tracked = this.trackedElements.get(element);
    if (!tracked || tracked.tracked) {
      return;
    }

    if (element.hasAttribute('data-tula-item') && tracked.itemVariant) {
      this.trackItemView(element, tracked.itemVariant);
      tracked.tracked = true;
    }
  }

  private async trackStoreView(element: Element): Promise<void> {
    if (!this.sdk.isInitialized()) {
      debugLog(this.debug, 'AutoTracker: SDK not initialized, skipping store view');
      return;
    }

    try {
      const properties = this.parseProperties(element);
      await this.sdk.logEvent('store_view', {
        ...properties,
        auto_tracked: true,
        element_id: element.id || undefined,
        timestamp: new Date().toISOString()
      });

      this.trackedElements.set(element, {
        element,
        tracked: true
      });

      debugLog(this.debug, 'AutoTracker: Tracked store view for element:', element);
    } catch (error) {
      console.error('[Tula SDK] AutoTracker failed to track store view:', error);
    }
  }

  private async trackItemView(element: Element, itemVariant: ItemVariantResponse): Promise<void> {
    if (!this.sdk.isInitialized()) {
      debugLog(this.debug, 'AutoTracker: SDK not initialized, skipping item view');
      return;
    }

    try {
      const properties = this.parseProperties(element);
      await this.sdk.logEvent('experiment_view', {
        itemId: itemVariant.itemId,
        skuVariantId: itemVariant.variant.skuVariantId,
        experimentId: itemVariant.experimentId,
        experimentArmId: itemVariant.experimentArmId,
        isControl: itemVariant.isControl,
        price: itemVariant.variant.price.cents,
        quantity: itemVariant.variant.quantity,
        ...properties,
        auto_tracked: true,
        element_id: element.id || undefined,
        timestamp: new Date().toISOString()
      });

      debugLog(this.debug, 'AutoTracker: Tracked experiment view for item:', itemVariant.itemId);
    } catch (error) {
      console.error('[Tula SDK] AutoTracker failed to track item view:', error);
    }
  }

  private parseProperties(element: Element): Record<string, any> {
    const propertiesAttr = element.getAttribute('data-tula-properties');
    if (!propertiesAttr) {
      return {};
    }

    try {
      return JSON.parse(propertiesAttr);
    } catch (error) {
      console.warn('[Tula SDK] AutoTracker: Invalid JSON in data-tula-properties:', propertiesAttr);
      return {};
    }
  }

  private parseItemVariant(element: Element): ItemVariantResponse | undefined {
    const variantAttr = element.getAttribute('data-tula-variant');
    if (!variantAttr) {
      console.warn('[Tula SDK] AutoTracker: data-tula-item element missing data-tula-variant attribute');
      return undefined;
    }

    try {
      return JSON.parse(variantAttr) as ItemVariantResponse;
    } catch (error) {
      console.warn('[Tula SDK] AutoTracker: Invalid JSON in data-tula-variant:', variantAttr);
      return undefined;
    }
  }

  // Utility methods for manual element tracking (DOM environments)
  trackElementAsStore(element: Element, properties?: Record<string, any>): void {
    if (properties) {
      element.setAttribute('data-tula-properties', JSON.stringify(properties));
    }
    element.setAttribute('data-tula-store', '');
    this.handleStoreElement(element);
  }

  trackElementAsItem(element: Element, itemVariant: ItemVariantResponse, properties?: Record<string, any>): void {
    element.setAttribute('data-tula-variant', JSON.stringify(itemVariant));
    if (properties) {
      element.setAttribute('data-tula-properties', JSON.stringify(properties));
    }
    element.setAttribute('data-tula-item', '');
    this.handleItemElement(element);
  }

  // Manual tracking methods for React Native and other non-DOM environments
  async trackStoreViewManual(properties?: Record<string, any>): Promise<void> {
    if (!this.sdk.isInitialized()) {
      debugLog(this.debug, 'AutoTracker: SDK not initialized, skipping manual store view');
      return;
    }

    try {
      await this.sdk.logEvent('store_view', {
        ...properties,
        manual_tracked: true,
        timestamp: new Date().toISOString()
      });

      debugLog(this.debug, 'AutoTracker: Manually tracked store view');
    } catch (error) {
      console.error('[Tula SDK] AutoTracker failed to manually track store view:', error);
    }
  }

  async trackItemViewManual(itemId: string, platform: 'ios' | 'android' | 'both' = 'both', properties?: Record<string, any>): Promise<void> {
    if (!this.sdk.isInitialized()) {
      debugLog(this.debug, 'AutoTracker: SDK not initialized, skipping manual item view');
      return;
    }

    try {
      // Get the item variant first
      const itemVariant = await this.sdk.getItemVariant(itemId, platform);
      
      await this.sdk.logEvent('experiment_view', {
        itemId: itemVariant.itemId,
        skuVariantId: itemVariant.variant.skuVariantId,
        experimentId: itemVariant.experimentId,
        experimentArmId: itemVariant.experimentArmId,
        isControl: itemVariant.isControl,
        price: itemVariant.variant.price.cents,
        quantity: itemVariant.variant.quantity,
        ...properties,
        manual_tracked: true,
        timestamp: new Date().toISOString()
      });

      debugLog(this.debug, 'AutoTracker: Manually tracked item view for:', itemId);
    } catch (error) {
      console.error('[Tula SDK] AutoTracker failed to manually track item view:', error);
    }
  }

  // React Native component tracking hooks
  trackReactNativeStoreView(properties?: Record<string, any>): void {
    // Fire and forget for React Native
    this.trackStoreViewManual(properties).catch(error => {
      console.error('[Tula SDK] React Native store view tracking failed:', error);
    });
  }

  trackReactNativeItemView(itemId: string, platform: 'ios' | 'android' | 'both' = 'both', properties?: Record<string, any>): void {
    // Fire and forget for React Native
    this.trackItemViewManual(itemId, platform, properties).catch(error => {
      console.error('[Tula SDK] React Native item view tracking failed:', error);
    });
  }
}